import { Link } from "react-router-dom";

import AddToCartButton from "../../../../../components/Form/AddToCartButton/AddToCartButton";
import FormWrapper, { preventDefault } from "../../../../../components/Form/FormWrapper";
import PlusMinusInput from "../../../../../components/Form/PlusMinusInput/PlusMinusInput";
import { missingImageURL } from "../../../../../helpers/images";
import useAPI from "../../../../../hooks/useAPI";
import useProductFlags from "../../../../../hooks/useProductFlags";
import Sitemap from "../../../../../routes/sitemap";
import { useState } from "react";
import Stickers from "./Stickers";
import icons from "helpers/icons";
import scss from "./ProductGridItem.module.scss";
import { Prices } from "../../../../../components/prices";
import { useTranslation } from "react-i18next";
import { isPurchaseAllowed } from "../../../../../helpers/product";

/**
 * Component that shows a single product in a grid.
 *
 * @param {{}} product The product to show.
 *
 * @return {JSX.Element}
 * @constructor
 */
const ProductGridItem = ({ product, translateKey }) => {
    const { t } = useTranslation();

    const productFlags = useProductFlags();
    const api = useAPI();
    const [loading, setLoading] = useState(false);

    // Track the quantity to add to the cart
    const [quantity, setQuantity] = useState(product?.behaviours?.cart?.default_loop_quantity ?? 1);

    const getQuantity = () => {
        if (quantity === 0) {
            setQuantity(product?.behaviours?.cart?.default_loop_quantity ?? 1);
            return product?.behaviours?.cart?.default_loop_quantity ?? 1;
        }
        return quantity;
    };

    const can_purchase = isPurchaseAllowed(product?.behaviours?.cart);
    const can_add_to_cart = product?.price?.price_defined && product?.price?.price?.original > 0 && product?.inventory?.inventory_defined && can_purchase;

    // Handle wishlist
    const inWishlist = productFlags.wishlist.includes(product);
    const wishlistClasses = [scss.wishlist];
    if (inWishlist) {
        wishlistClasses.push(scss.active);
    }

    return (
        <div className={scss.wrapper}>
            <div className={scss.product}>
                <div className={scss.productImageContainer}>
                    {/* Wishlist */}
                    <button onClick={preventDefault(() => api.globalAddToWishlist(product?.basic_data?.id_product, !inWishlist))} className={wishlistClasses.join(" ")}>
                        <img src={icons.wish_list} alt="" />
                    </button>
                    {/* Additional stickers */}
                    {product?.stickers?.length > 0 && <Stickers stickers={product?.stickers} />}

                    {/* Image */}
                    <Link to={Sitemap.catalogue.productDetails + "/" + product?.id} className={scss.productImage}>
                        <div className={scss.imageContainer}>
                            <img src={product?.images[0] ?? missingImageURL()} alt={product?.name} className={`${scss.image} ${product?.images[1] && scss.productImage1}`} />
                            {product?.images[1] && <img src={product?.images[1]} alt={product?.name} className={`${scss.image} ${scss.productImage2}`} />}
                        </div>
                    </Link>
                </div>

                <Link to={Sitemap.catalogue.productDetails + "/" + product?.id} className={scss.product}>
                    <span className={`${scss.category} mobile-hidden`}>{product?.categories[0]?.name}</span>
                    <span className={scss.name}>{product?.basic_data?.name}</span>

                    <span className={scss.code}>
                        <span className={scss.codeTitle}>{t(`${translateKey}-text-Code`)}:</span>
                        <span className={scss.codeInt}>{product?.basic_data?.sku}</span>
                    </span>

                    <Prices price={product?.price} inventory={product?.inventory} behaviours={product?.behaviours?.cart} />
                </Link>
            </div>

            {/* Add to cart */}
            <FormWrapper
                className={`row pt-2 ${scss.addToCart} ${can_add_to_cart && product?.product_type !== "variant" ? scss.cartControls : ""}`}
                onSubmit={() => {
                    if (!can_add_to_cart) return;
                    setLoading(true);
                    api.globalAddToCart(product.id, getQuantity(), loading, setLoading);
                    setQuantity(product?.behaviours?.cart?.default_loop_quantity);
                }}
            >
                <div className={product?.product_type === "variant" ? "col-12" : "col-6"}>
                    {product?.product_type === "variant" ? (
                        <Link className={scss.button1} to={`/katalog/artikal/${product?.basic_data?.id_product}`}>
                            <span>{t(`${translateKey}-button-product_link`)}</span>
                            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 10h12m-5-5 5 5-5 5" /></svg>
                        </Link>
                    ) : can_add_to_cart ? (
                        <PlusMinusInput 
                            key={product?.id + "_ProductGridItem"}
                            behaviours={product?.behaviours?.cart}
                            max={Number(product?.inventory?.amount)}
                            quantity={Number(quantity)}
                            setQuantity={setQuantity}
                            debounceId={product?.id + "_web"}
                        />
                    ) : null}
                </div>
                <div className={can_add_to_cart ? `col-6` : `col-11 p-0 m-auto`}>
                    {can_add_to_cart ? (
                        product?.product_type === "variant" ? null : (
                            <>
                                <AddToCartButton
                                    translateKey={`${translateKey}-button`}
                                    iconOnly
                                    loading={loading}
                                    type={product?.inventory?.amount || product?.item?.inventory <= 0 ? "submit" : "button"}
                                    product={product}
                                />
                            </>
                        )
                    ) : product?.product_type !== "variant" ? (
                        <>
                                <Link to={`/kontakt?slug=${product?.slug ?? product?.id}`} className={scss.button2}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m21 3-6.5 18-4-7.5L3 9.5 21 3ZM21 3 10.5 13.5" /></svg>
                                    {t(`${translateKey}-button-contact_link`)}
                                </Link>
                        </>
                    ) : null}
                </div>
            </FormWrapper>
        </div>
    );
};

export default ProductGridItem;
